import { assetsClient } from '@/clients/assets-client.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';
import type { AssetType } from '@/services/asset-types-cache.js';
import { backboneService } from '@/services/backbone.js';
import { codesService } from '@/services/codes.js';
import { HatElement } from '@/components/hat-element.js';

class HatAssetForm extends HatElement {
    private _locationId: string | null = null;
    private _assetId: string | null = null;
    private _assetTypes: AssetType[] = [];
    private _selectedCategory: string | null = null;

    static get observedAttributes() {
        return ['location-id', 'asset-id'];
    }

    attributeChangedCallback(name: string, _oldVal: string | null, newVal: string | null) {
        if (name === 'location-id') this._locationId = newVal;
        if (name === 'asset-id') this._assetId = newVal;
    }

    async connectedCallback() {
    super.connectedCallback();
    this._locationId = this.getAttribute('location-id');
        this._assetId = this.getAttribute('asset-id');
        this._renderShell();
        this._assetTypes = await assetTypesCache.getAll();
        await this._populateCategoryPills();
        this._populateTypeSelect();
        this._bindEvents();
        if (this._assetId) await this._loadData();
    }

    // ─── Data ─────────────────────────────────────────────────────────────────

    async _loadData() {
        const asset = await assetsClient.getAsset(this._locationId!, this._assetId!);
        this._populateForm(asset);
    }

    _populateForm(asset: any) {
        const form = this.root.querySelector('form') as any;
        form.name.value = asset.name ?? '';

        if (asset.assetType) {
            const select = this.root.querySelector<HTMLSelectElement>('select[name="assetType"]')!;
            select.value = asset.assetType;
            // If the type isn't in the current filtered list, reset category to All
            if (!select.value) {
                this._populateTypeSelect(null);
                select.value = asset.assetType;
                this.root.querySelectorAll<HTMLElement>('.category-pill').forEach(p => {
                    p.classList.toggle('active', p.dataset.category === '');
                });
            }
            this._renderProperties(asset.assetType);

            // Fill in existing property values
            (asset.properties ?? []).forEach((p: any) => {
                const input = this.root.querySelector<HTMLInputElement>(`[data-prop-code="${p.code}"]`);
                if (input) input.value = p.value ?? '';
            });
        }

        const gallery = this.root.querySelector('hat-location-gallery')!;
        gallery.setImages(asset.images ?? []);
        if (asset.displayImage?.id) {
            gallery.setAttribute('primary-image-id', asset.displayImage.id);
        }
    }

    // ─── Rendering ────────────────────────────────────────────────────────────

    _renderShell() {
        this.root.innerHTML = `
            <style>
                .container { max-width: 900px; margin: 20px auto; }

                .card {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                form { display: flex; flex-direction: column; gap: 12px; }

                input, select {
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    flex: 1;
                    box-sizing: border-box;
                    font-size: 14px;
                }

                .fields {
                    display: grid;
                    grid-template-columns: max-content 1fr;
                    gap: 10px 12px;
                    align-items: center;
                }

                .field {
                    display: contents;
                }

                .field label {
                    font-size: 13px;
                    color: #555;
                    text-align: right;
                    white-space: nowrap;
                }

                .section-label {
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #888;
                    margin-bottom: 4px;
                }

                .category-pills {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-bottom: 8px;
                }

                .category-pill {
                    padding: 4px 12px;
                    border-radius: 20px;
                    border: 1px solid #ddd;
                    background: #f5f5f5;
                    cursor: pointer;
                    font-size: 13px;
                    white-space: nowrap;
                }

                .category-pill.active {
                    background: #111;
                    color: white;
                    border-color: #111;
                }

                .properties-section { display: flex; flex-direction: column; gap: 8px; }

                .capture-tabs { display: flex; gap: 8px; }

                .mode-btn {
                    flex: 1;
                    border: none;
                    padding: 10px;
                    border-radius: 10px;
                    cursor: pointer;
                    background: #eee;
                }

                .mode-btn.active { background: #111; color: white; }

                .actions {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 10px;
                }

                .save {
                    background: #111;
                    color: white;
                    border: none;
                    padding: 10px 16px;
                    border-radius: 10px;
                    cursor: pointer;
                }

                .save:disabled { opacity: 0.5; cursor: not-allowed; }

                .modal {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.92);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 20px;
                    box-sizing: border-box;
                }

                .modal hat-image {
                    width: auto;
                    height: auto;
                    max-width: 90vw;
                    max-height: 90vh;
                    border-radius: 12px;
                }

                .spinner-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }

                .spinner-box {
                    background: white;
                    border-radius: 16px;
                    padding: 32px 40px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
                }

                .spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #eee;
                    border-top-color: #111;
                    border-radius: 50%;
                    animation: spin 0.75s linear infinite;
                }

                .spinner-label { font-size: 15px; font-weight: 500; color: #444; }

                @keyframes spin { to { transform: rotate(360deg); } }
            </style>

            <div class="container">
                <div class="card">
                    <form>
                        <div class="fields">
                            <div class="field">
                                <label>Asset Name</label>
                                <input name="name" placeholder="(optional)" autocomplete="off" />
                            </div>
                        </div>

                        <div>
                            <div class="section-label">Asset Type</div>
                            <div class="category-pills"></div>
                            <select name="assetType">
                                <option value="">Select type…</option>
                            </select>
                        </div>

                        <div class="properties-section"></div>

                        <div>
                            <div class="section-label">Photos</div>
                            <div class="capture-tabs">
                                <button class="mode-btn active" type="button" data-mode="camera">Camera</button>
                                <button class="mode-btn" type="button" data-mode="upload">Upload</button>
                            </div>
                            <div class="capture-container">
                                <hat-camera-capture></hat-camera-capture>
                                <hat-file-upload style="display:none"></hat-file-upload>
                            </div>
                            <hat-location-gallery mode="edit"></hat-location-gallery>
                        </div>

                        <div class="actions">
                            <button class="save" type="submit">Save</button>
                            <hat-nav-button href="/app/locations/${this._locationId ?? ''}">Cancel</hat-nav-button>
                        </div>
                    </form>
                </div>
            </div>
        `;
    }

    async _populateCategoryPills() {
        const container = this.root.querySelector<HTMLElement>('.category-pills')!;
        const categories = [...new Set(
            this._assetTypes.map(t => t.category).filter((c): c is string => !!c)
        )];

        if (!categories.length) return;

        const categoryCodes = await codesService.getDomain('assets.categories');
        const labelMap = new Map(categoryCodes.map(c => [c.code, c.label]));

        container.innerHTML = ['All', ...categories].map(cat =>
            `<button type="button" class="category-pill${cat === 'All' ? ' active' : ''}" data-category="${cat === 'All' ? '' : cat}">${cat === 'All' ? 'All' : (labelMap.get(cat) ?? cat)}</button>`
        ).join('');
    }

    _populateTypeSelect(category: string | null = null) {
        const select = this.root.querySelector<HTMLSelectElement>('select[name="assetType"]')!;
        const filtered = category
            ? this._assetTypes.filter(t => t.category === category)
            : this._assetTypes;

        select.innerHTML = '<option value="">Select type…</option>';
        filtered.forEach(t => {
            const opt = document.createElement('option');
            opt.value = t.code ?? '';
            opt.textContent = t.description ?? t.code ?? '';
            select.appendChild(opt);
        });
    }

    _renderProperties(typeCode: string) {
        const section = this.root.querySelector<HTMLElement>('.properties-section')!;
        const type = this._assetTypes.find(t => t.code === typeCode);

        if (!type?.properties?.length) {
            section.innerHTML = '';
            return;
        }

        section.innerHTML = `
            <div class="section-label">Properties</div>
            <div class="fields">
                ${type.properties.map(p => `
                    <div class="field">
                        <label>${p.description ?? p.code}</label>
                        <input
                            name="prop_${p.code}"
                            data-prop-code="${p.code}"
                        />
                    </div>
                `).join('')}
            </div>
        `;
    }

    // ─── Events ───────────────────────────────────────────────────────────────

    _bindEvents() {
        const form     = this.root.querySelector<HTMLFormElement>('form')!;
        const camera   = this.root.querySelector<HTMLElement>('hat-camera-capture')!;
        const uploader = this.root.querySelector<HTMLElement>('hat-file-upload')!;
        const gallery  = this.root.querySelector('hat-location-gallery')!;
        const saveBtn  = this.root.querySelector<HTMLButtonElement>('.save')!;
        const typeSelect = this.root.querySelector<HTMLSelectElement>('select[name="assetType"]')!;

        this.root.querySelector<HTMLElement>('.category-pills')!.addEventListener('click', (e) => {
            const pill = (e.target as HTMLElement).closest<HTMLElement>('.category-pill');
            if (!pill) return;
            this.root.querySelectorAll<HTMLElement>('.category-pill').forEach(p => p.classList.remove('active'));
            pill.classList.add('active');
            this._selectedCategory = pill.dataset.category || null;
            this._populateTypeSelect(this._selectedCategory);
            this._renderProperties('');
        });

        typeSelect.addEventListener('change', () => {
            this._renderProperties(typeSelect.value);
        });

        [camera, uploader].forEach(source => {
            source.addEventListener('photo-captured', (e) => {
                gallery.addPhoto((e as CustomEvent).detail);
            });
        });

        gallery.addEventListener('image-expanded', (e: Event) => {
            this._openImage((e as CustomEvent).detail);
        });

        this.root.querySelectorAll<HTMLElement>('.mode-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.root.querySelectorAll<HTMLElement>('.mode-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                camera.style.display = btn.dataset.mode === 'camera' ? '' : 'none';
                uploader.style.display = btn.dataset.mode === 'upload' ? '' : 'none';
            });
        });

        form.onsubmit = async (e) => {
            e.preventDefault();
            saveBtn.disabled = true;
            this._showSpinner();

            try {
                await this._save(form, gallery);
                backboneService.navigateTo(`/app/locations/${this._locationId}`);
            } catch (err) {
                console.error('Save failed', err);
                this._hideSpinner();
                saveBtn.disabled = false;
            }
        };
    }

    // ─── Save ─────────────────────────────────────────────────────────────────

    async _save(form: HTMLFormElement, gallery: any) {
        const propInputs = [...this.root.querySelectorAll<HTMLInputElement>('[data-prop-code]')];
        const propsArray = propInputs
            .filter(input => input.value)
            .map(input => ({ code: input.dataset.propCode!, dataType: 'string', value: input.value }));

        if (this._assetId) {
            // Update — JSON body, upload new photos separately then resolve display image
            let uploadedImages: any[] = [];
            if (gallery.newPhotos.length > 0) {
                const fd = new FormData();
                gallery.newPhotos.forEach((p: any) => fd.append('files', p.blob));
                uploadedImages = await assetsClient.uploadAssetImages(this._locationId!, this._assetId, fd);
            }

            // If the primary is a newly uploaded photo, resolve its server id
            let displayImageId = gallery.primaryImageId;
            const newPhotoIndex = gallery.newPhotos.findIndex(
                (p: any) => p.tempId === gallery.primaryImageId
            );
            if (newPhotoIndex >= 0 && uploadedImages[newPhotoIndex]) {
                displayImageId = uploadedImages[newPhotoIndex].id;
            }

            const f = form as any;
            await assetsClient.updateAsset(this._locationId!, this._assetId, {
                name: f.name.value,
                assetType: f.assetType.value,
                properties: propsArray,
                displayImageId: displayImageId ?? undefined
            });
        } else {
            // Create — multipart with files and displayImageIndex
            const fd = new FormData(form);
            if (propsArray.length) fd.append('properties', JSON.stringify(propsArray));
            [...fd.keys()].filter(k => k.startsWith('prop_')).forEach(k => fd.delete(k));

            gallery.newPhotos.forEach((p: any) => fd.append('files', p.blob));

            let displayIndex = 0;
            if (gallery.primaryImageId) {
                const idx = gallery.newPhotos.findIndex((p: any) => p.tempId === gallery.primaryImageId);
                if (idx >= 0) displayIndex = idx;
            }
            fd.append('displayImageIndex', displayIndex.toString());

            await assetsClient.createAsset(this._locationId!, fd);
        }
    }

    // ─── Spinner ──────────────────────────────────────────────────────────────

    _showSpinner() {
        const spinner = document.createElement('div');
        spinner.className = 'spinner-overlay';
        spinner.innerHTML = `
            <div class="spinner-box">
                <div class="spinner"></div>
                <div class="spinner-label">Saving…</div>
            </div>
        `;
        this.root.appendChild(spinner);
    }

    _hideSpinner() {
        this.root.querySelector('.spinner-overlay')?.remove();
    }

    // ─── Modal ────────────────────────────────────────────────────────────────

    _openImage({ documentId, url }: { documentId?: string; url?: string }) {
        const modal = document.createElement('div');
        modal.className = 'modal';

        let content: HTMLElement;

        if (documentId) {
            content = document.createElement('hat-image');
            content.setAttribute('type', 'expanded');
            content.setAttribute('document-id', documentId);
        } else {
            content = document.createElement('img');
            (content as HTMLImageElement).src = url ?? '';
        }

        modal.appendChild(content);
        modal.onclick = () => modal.remove();
        this.root.appendChild(modal);
    }
}

customElements.define('hat-asset-form', HatAssetForm);